import type { WriteStamp } from './write-operation';

const DEVICE_ID_STORAGE_KEY = 'on-book-pro.writeClock.deviceId';
const LOGICAL_COUNTER_STORAGE_KEY = 'on-book-pro.writeClock.logicalCounter';

let fallbackDeviceId: string | null = null;
let fallbackLogicalCounter = 0;
let logicalCounterWriteFailed = false;

function getStorage(): Storage | null {
    try {
        return globalThis.localStorage ?? null;
    } catch {
        return null;
    }
}

function createId(): string {
    return globalThis.crypto?.randomUUID?.() ?? `write-${Math.random().toString(36).slice(2)}`;
}

function readStorageValue(key: string): { status: 'missing' | 'unavailable' | 'value'; value?: string } {
    const storage = getStorage();
    if (!storage) {
        return { status: 'unavailable' };
    }

    try {
        const value = storage.getItem(key);
        return value === null ? { status: 'missing' } : { status: 'value', value };
    } catch {
        return { status: 'unavailable' };
    }
}

function writeStorageValue(key: string, value: string): boolean {
    const storage = getStorage();
    if (!storage) {
        return false;
    }

    try {
        storage.setItem(key, value);
        return true;
    } catch {
        return false;
    }
}

function readLogicalCounter(): number {
    const storedValue = readStorageValue(LOGICAL_COUNTER_STORAGE_KEY);
    const storedCounter = storedValue.status === 'value' ? Number(storedValue.value) : Number.NaN;

    if (Number.isFinite(storedCounter) && storedCounter >= 0) {
        return Math.max(storedCounter, fallbackLogicalCounter);
    }

    return storedValue.status === 'missing' && !logicalCounterWriteFailed ? 0 : fallbackLogicalCounter;
}

function writeLogicalCounter(counter: number): void {
    fallbackLogicalCounter = counter;
    logicalCounterWriteFailed = !writeStorageValue(LOGICAL_COUNTER_STORAGE_KEY, String(counter));
}

function compareStrings(a: string, b: string): number {
    if (a === b) {
        return 0;
    }

    return a < b ? -1 : 1;
}

function compareNumbers(a: number, b: number): number {
    return a === b ? 0 : a < b ? -1 : 1;
}

export function getOrCreateDeviceId(): string {
    const storedDeviceId = readStorageValue(DEVICE_ID_STORAGE_KEY);

    if (storedDeviceId.status === 'value' && storedDeviceId.value) {
        fallbackDeviceId = storedDeviceId.value;
        return storedDeviceId.value;
    }

    const deviceId = fallbackDeviceId ?? createId();
    fallbackDeviceId = deviceId;
    writeStorageValue(DEVICE_ID_STORAGE_KEY, deviceId);
    return deviceId;
}

export function createWriteStamp(actorId: string): WriteStamp {
    const deviceId = getOrCreateDeviceId();
    const logicalCounter = readLogicalCounter() + 1;
    writeLogicalCounter(logicalCounter);

    return {
        clientWriteId: `${deviceId}:${logicalCounter}:${createId()}`,
        actorId,
        deviceId,
        wallTimeMs: Date.now(),
        logicalCounter,
    };
}

export function compareWriteStamps(a: WriteStamp, b: WriteStamp): number {
    if (a.deviceId === b.deviceId) {
        return (
            compareNumbers(a.logicalCounter, b.logicalCounter) ||
            compareNumbers(a.wallTimeMs, b.wallTimeMs) ||
            compareStrings(a.clientWriteId, b.clientWriteId) ||
            compareStrings(a.actorId, b.actorId) ||
            compareStrings(a.deviceId, b.deviceId)
        );
    }

    return (
        compareNumbers(a.wallTimeMs, b.wallTimeMs) ||
        compareNumbers(a.logicalCounter, b.logicalCounter) ||
        compareStrings(a.clientWriteId, b.clientWriteId) ||
        compareStrings(a.actorId, b.actorId) ||
        compareStrings(a.deviceId, b.deviceId)
    );
}
